import { getEffectiveUserStatus } from '@modules/user/utils/getEffectiveUserStatus';
import {
  IconButton,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { UserStatus } from 'src/@types/user-status';
import { IWorkspaceUser, UserType } from 'src/@types/workspace-info';
import { useAuthContext } from 'src/auth/useAuthContext';
import BadgeStatus from 'src/components/badge-status';
import { CustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import MenuPopover from 'src/components/menu-popover';
import Scrollbar from 'src/components/scrollbar';
import { useLocales } from 'src/locales';
import { FormatTime } from 'src/utils/formatTime';
import { UserCustomStatus } from '../../../modules/user-status/UserCustomStatus/UserCustomStatus';

const ITEM_HEIGHT = 64;

type Props = {
  users?: IWorkspaceUser[];
};

export function OnlineUsersPopover({ users: workspaceUsers }: Props) {
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
  const { user: currentUser } = useAuthContext();
  const { translate } = useLocales();

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const users = useMemo(() => {
    return [...(workspaceUsers || [])]
      .filter((u) => u._id !== currentUser?._id && u.type === UserType.USER)
      .sort((a, b) => {
        const aStatus = getEffectiveUserStatus(a);
        const bStatus = getEffectiveUserStatus(b);

        if (aStatus === UserStatus.ONLINE && bStatus !== UserStatus.ONLINE) {
          return -1;
        }
        if (aStatus !== UserStatus.ONLINE && bStatus === UserStatus.ONLINE) {
          return 1;
        }

        return a.name.localeCompare(b.name);
      });
  }, [workspaceUsers, currentUser?._id]);

  const onlineUsers = useMemo(
    () => users.filter((u) => u.status === UserStatus.ONLINE),
    [users],
  );

  return (
    <>
      <Tooltip
        placement="bottom"
        arrow
        title={translate('shared.labels.workspace_members')}
      >
        <IconButton
          color="inherit"
          onClick={handleOpenPopover}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            ...(openPopover && {
              bgcolor: (theme) => theme.palette.action.selected,
            }),
          }}
        >
          <Iconify icon="tabler:users" />
        </IconButton>
      </Tooltip>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 320, mt: 1.5 }}
      >
        <Typography variant="h6" sx={{ p: 1.5 }}>
          {translate('header.online_users_dropdown.title')}{' '}
          <Typography component="span">({onlineUsers.length})</Typography>
        </Typography>

        <Scrollbar sx={{ height: ITEM_HEIGHT * 6 }}>
          {users.map((user) => (
            <MenuItem key={user._id} sx={{ height: ITEM_HEIGHT }}>
              <ListItemAvatar>
                <CustomAvatar
                  src={user.picture}
                  name={user.name}
                  BadgeProps={{
                    badgeContent: (
                      <BadgeStatus status={getEffectiveUserStatus(user)} />
                    ),
                  }}
                />
              </ListItemAvatar>

              <ListItemText
                primary={user.name}
                secondary={
                  user.status === UserStatus.OFFLINE ? (
                    FormatTime.ToNow(user.lastAccess)
                  ) : (
                    <UserCustomStatus user={user} />
                  )
                }
                primaryTypographyProps={{
                  typography: 'subtitle2',
                  sx: { mb: 0.25 },
                }}
                secondaryTypographyProps={{ typography: 'caption' }}
              />
            </MenuItem>
          ))}
        </Scrollbar>
      </MenuPopover>
    </>
  );
}
